import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { hotelService, authService, api } from '../../services/api';
import HotelForm from './components/HotelForm';
import HotelCard from './components/HotelCard';
import HotelTable from './components/HotelTable';

const HotelSettings = () => {
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHotelForm, setShowHotelForm] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Carregar hotéis
  useEffect(() => {
    loadHotels();
  }, []);

  const loadHotels = async () => {
    try {
      setLoading(true);
      const hotelsData = await hotelService.getAll();
      setHotels(hotelsData);
    } catch (error) {
      console.error('Erro ao carregar hotéis:', error);
      setHotels([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar hotéis
  const filteredHotels = hotels.filter(hotel => {
    const matchesSearch = hotel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hotel.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hotel.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && hotel.isActive) ||
                         (filterStatus === 'inactive' && !hotel.isActive);
    
    return matchesSearch && matchesStatus;
  });

  // Ações do hotel
  const handleAddHotel = () => {
    setSelectedHotel(null);
    setFormMode('create');
    setShowHotelForm(true);
  };

  const handleEditHotel = (hotel) => {
    setSelectedHotel(hotel);
    setFormMode('edit');
    setShowHotelForm(true);
  };

  const handleDeleteHotel = async (hotel) => {
    if (window.confirm(`Tem certeza que deseja excluir o hotel "${hotel.name}"?`)) {
      try {
        await hotelService.delete(hotel.id);
        await loadHotels();
        alert('Hotel excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir hotel:', error);
        alert('Erro ao excluir hotel. Tente novamente.');
      }
    }
  };

  const handleSaveHotel = async (hotelData) => {
    try {
      if (formMode === 'edit') {
        await hotelService.update(selectedHotel.id, hotelData);
        alert('Hotel atualizado com sucesso!');
        // Persistir configuração de idade mínima para cobrança de criança, se informada
        if (hotelData.minChildChargeAge !== undefined && hotelData.minChildChargeAge !== '') {
          try {
            await hotelService.setSetting(selectedHotel.id, 'MIN_CHILD_CHARGE_AGE', Number(hotelData.minChildChargeAge), 'NUMBER', 'Idade mínima para cobrança de criança');
          } catch (e) {
            console.warn('Falha ao salvar configuração de idade mínima:', e);
          }
        }
        
        // Persistir configuração de desconto percentual para crianças, se informada
        if (hotelData.childDiscountPercent !== undefined && hotelData.childDiscountPercent !== '') {
          try {
            await hotelService.setSetting(selectedHotel.id, 'CHILD_DISCOUNT_PERCENT', Number(hotelData.childDiscountPercent), 'NUMBER', 'Desconto percentual aplicado ao preço solteiro para crianças cobradas');
          } catch (e) {
            console.warn('Falha ao salvar configuração de desconto percentual:', e);
          }
        }
        
        // Limpar cache de hotéis após atualização
        try {
          await hotelService.clearCache();
          console.log('✅ Cache de hotéis limpo após atualização');
        } catch (cacheError) {
          console.log('⚠️ Erro ao limpar cache:', cacheError);
        }
        
        // Notificar o cabeçalho sobre a mudança no hotel
        const updatedHotel = { ...selectedHotel, ...hotelData };
        console.log('🔄 Disparando evento hotelChanged:', updatedHotel);
        window.dispatchEvent(new CustomEvent('hotelChanged', { 
          detail: { hotel: updatedHotel } 
        }));
        
        // Se o hotel atualizado for o hotel selecionado no cabeçalho, atualizar o localStorage
        const currentSelectedHotel = authService.getSelectedHotel();
        if (currentSelectedHotel && currentSelectedHotel.id === selectedHotel.id) {
          console.log('🔄 Atualizando hotel selecionado no localStorage:', updatedHotel);
          authService.setSelectedHotel(updatedHotel);
          
          // Atualizar o token para refletir as mudanças no hotel
          try {
            const response = await api.post('/hotels/primary', { hotelId: updatedHotel.id });
            if (response.data.newToken) {
              localStorage.setItem('token', response.data.newToken);
              console.log('🔄 Token atualizado após edição do hotel');
            }
          } catch (error) {
            console.log('⚠️ Erro ao atualizar token:', error);
          }
        }
      } else {
        await hotelService.create(hotelData);
        alert('Hotel criado com sucesso!');
        
        // Limpar cache de hotéis após criação
        try {
          await hotelService.clearCache();
          console.log('✅ Cache de hotéis limpo após criação');
        } catch (cacheError) {
          console.log('⚠️ Erro ao limpar cache:', cacheError);
        }
      }
      
      setShowHotelForm(false);
      setSelectedHotel(null);
      await loadHotels();
    } catch (error) {
      console.error('Erro ao salvar hotel:', error);
      alert('Erro ao salvar hotel. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
        <Sidebar isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        <main className="lg:ml-60 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="text-muted-foreground">Carregando hotéis...</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
      <Sidebar isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      
      <main className="lg:ml-60 pt-16">
        <div className="p-6">
          {/* Breadcrumb */}
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Gerenciamento de Hotéis</h1>
              <p className="text-muted-foreground">
                Gerencie todos os hotéis do sistema
              </p>
            </div>
            
            <div className="flex items-center space-x-4 mt-4 lg:mt-0">
              <Button 
                variant="default" 
                iconName="Plus" 
                iconPosition="left"
                onClick={handleAddHotel}
              >
                Adicionar Hotel
              </Button>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="relative">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Buscar hotéis..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">Todos os Status</option>
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  iconName="Grid"
                >
                  Cards
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  iconName="List"
                >
                  Tabela
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          {filteredHotels.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Building2" size={24} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {searchTerm || filterStatus !== 'all' ? 'Nenhum hotel encontrado' : 'Nenhum hotel cadastrado'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Tente ajustar os filtros de busca.'
                  : 'Comece criando o primeiro hotel do sistema.'
                }
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <Button onClick={handleAddHotel} variant="default">
                  Criar Primeiro Hotel
                </Button>
              )}
            </div>
          ) : (
            <>
              {viewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredHotels.map((hotel) => (
                    <HotelCard
                      key={hotel.id}
                      hotel={hotel}
                      onEdit={() => handleEditHotel(hotel)}
                      onDelete={() => handleDeleteHotel(hotel)}
                    />
                  ))}
                </div>
              ) : (
                <HotelTable
                  hotels={filteredHotels}
                  onEdit={handleEditHotel}
                  onDelete={handleDeleteHotel}
                />
              )}
            </>
          )}

          {/* Stats */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Building2" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total de Hotéis</p>
                  <p className="text-2xl font-bold text-foreground">{hotels.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Hotéis Ativos</p>
                  <p className="text-2xl font-bold text-foreground">
                    {hotels.filter(h => h.isActive).length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="XCircle" size={20} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Hotéis Inativos</p>
                  <p className="text-2xl font-bold text-foreground">
                    {hotels.filter(h => !h.isActive).length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-info/10 rounded-lg flex items-center justify-center">
                  <Icon name="MapPin" size={20} className="text-info" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cidades</p>
                  <p className="text-2xl font-bold text-foreground">
                    {new Set(hotels.map(h => h.city)).size}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Hotel Form Modal */}
      {showHotelForm && (
        <HotelForm
          hotel={selectedHotel}
          mode={formMode}
          isOpen={showHotelForm}
          onClose={() => {
            setShowHotelForm(false);
            setSelectedHotel(null);
          }}
          onSave={handleSaveHotel}
        />
      )}
    </div>
  );
};

export default HotelSettings;
